import { FormInstance, message } from 'antd'
import { PropertyType } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { PTOTypeEnum, TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import { PtoV3SellingStatusEnum } from 'constants/pto-v3'
import {
  buildExpectedReturnCalculator,
  cleanCoTrustedPartners,
  describeV3ContractError,
  INITIAL_CO_TRUSTED_PARTNER,
} from 'extends/pto-v3.extend'
import { useDeployPtoV3 } from 'hooks/pto-v3/use-deploy-pto-v3'
import { PAGES } from 'routes'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError, logInfo } from 'utils/logger'

interface UseCreatePTOV3Props {
  form: FormInstance
}

/**
 * PTO v3 (ERC-3643) create flow - same as PTO v2 (create on the BE, Off Sale, no
 * price/fee/timelines) except the on-chain step: the ERC-3643 token is deployed
 * through PTOOrchestrator.createPTO with the calldata the BE prepares.
 * A failed deploy is not fatal - the PTO can be deployed later from the list.
 */
const useCreatePTOV3 = ({ form }: UseCreatePTOV3Props) => {
  const navigate = useNavigate()
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { address } = useAccount()
  const { isAgent, deployPto, reportDeployed } = useDeployPtoV3()

  const handleGetData = useCallback(async () => {
    try {
      const resListPropertyType = await AppAdmin.getListProperty()
      if (resListPropertyType?.data.success) {
        setPropertyTypes(
          resListPropertyType.data.data.map((item: PropertyType) => ({
            ...item,
            id: Number(item.id),
          }))
        )
      }
      form.setFieldsValue({
        coTrustedPartners: [INITIAL_CO_TRUSTED_PARTNER],
        status: true,
        isShowDetail: true,
        isShowList: true,
      })
    } catch (error: any) {
      logError('Load property types error:', error)
    }
  }, [form])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const values = await form.validateFields()
      const {
        type,
        ptoType,
        name,
        contractName,
        symbol,
        country,
        description = '',
        status,
        isFeatured,
        isShowDetail,
        isShowList,
        latitude,
        longitude,
        propertyItems,
        totalSupply,
        tradeUnlockTime,
        ownerAddress,
        typeSocial,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      // Body of POST /admin/pto/v3 (Swagger): same as v2 minus royaltyFee, goldenTickets and
      // metadata, plus symbol.
      // The token cap (whole PTOs, 0 decimals) is sent under the API's v1/v2 field name maxNFTSupply.
      const params = {
        fullName: name,
        contractName: `${contractName || ''}`.trim(),
        symbol: `${symbol || ''}`.trim(),
        sellingStatus: PtoV3SellingStatusEnum.OFF_SALE,
        country,
        description,
        isFeatured: !!isFeatured,
        isShowDetail: !!isShowDetail,
        isShowList: !!isShowList,
        type,
        ptoType: ptoType || PTOTypeEnum.PTO,
        latitude,
        longitude,
        propertyItems,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        ownerAddress,
        tradeUnlockTime: tradeUnlockTime || 0,
        maxNFTSupply: totalSupply,
        typeSocial: typeSocial || {},
        terms: {
          content: terms || '',
          flowType: TermsFlowEnum.PURCHASE,
        },
        coTrustedPartners,
        expectedReturnCalculator: buildExpectedReturnCalculator(values),
      }

      onOpenModalProcessing()
      logInfo('Submitting PTO v3 with params:', params)

      const results = await AppAdmin.ptoV3Create(params)
      if (results.status === 200) {
        const createdPto = results.data.data
        // The live response carries the PTO id as `externalId` (Swagger says `_id`) - accept both
        const id: string = createdPto?.externalId || createdPto?._id
        if (!id) {
          throw new Error('The API did not return the id of the created PTO')
        }

        if (!address) {
          message.warning('PTO created. Connect a wallet to deploy it from the list.')
        } else if (isAgent === false) {
          message.warning(
            'PTO created, but your wallet is not an agent of the PTO Orchestrator. Deploy it later.'
          )
        } else {
          try {
            const result = await deployPto(address, id)
            // display switch of the form -> status, same as v2 (PATCH /:id/contract)
            await reportDeployed(
              id,
              result,
              status ? TokenizeStatusEnum.ACTIVE : TokenizeStatusEnum.INACTIVE
            )
            message.success('PTO created successfully!')
          } catch (error) {
            const errorMessage = describeV3ContractError(error)
            message.error(errorMessage || 'Deploy failed, you can deploy it later from the list!')
            logError('Error while deploying PTO v3:', error)
          }
        }
        navigate(PAGES.PTO_V3)
      }
    } catch (error: any) {
      const errorMessage = describeV3ContractError(error)
      message.error(errorMessage || 'Error while submitting form')
      logError('Error while submitting form:', errorMessage)
    } finally {
      onCloseModalProcessing()
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(PAGES.PTO_V3)
  }

  useEffect(() => {
    handleGetData()
  }, [handleGetData])

  return {
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    propertyTypes,
    isSubmitting,
    handleSubmit,
    handleCancel,
  }
}

export default useCreatePTOV3
